import React from 'react'
import type { Property } from 'csstype'

import { DEFAULT_LIGHT_CONTRAST_COLOR, findContrastColor } from '../services/color'
import { formatCentsToReais } from '../services/money'
import { classNames } from '../services/string'

import PlanBenefits from './Plans/PlanBenefits'
import { planBenefits } from './Plans/constants'
import ContactButton from './ContactButton'

interface PlanCardProps extends Omit<Queries.PlansContentFragment, 'id'> {
  className?: string
  name: string
  description: string
  metaDescription: string
  primaryColor?: Property.Color
  secondaryColor?: Property.Color
  blurred?: boolean
  redirectToTarget?: boolean
  planBenefitsTitle?: string
  customPlanBenefits?: string[]
}

const PlanCard = ({
  className,
  name,
  metaDescription,
  monthly,
  value,
  valueFrom,
  primaryColor,
  secondaryColor,
  blurred = false,
  planBenefitsTitle,
  customPlanBenefits,
  redirectToTarget,
}: PlanCardProps) => {
  const primaryContrastColor = findContrastColor(primaryColor || 'var(--color-primary-600)')
  const secondaryContrastColor = findContrastColor(secondaryColor || 'var(--color-primary-100)')

  return (
    <div
      className={classNames(
        className,
        'flex flex-col justify-between gap-4 rounded-xl border-t-2 border-l-2 p-4 shadow-lg drop-shadow-lg',
        blurred && 'backdrop-blur-md',
      )}
      style={{
        backgroundColor: `${secondaryColor || 'var(--color-primary-100)'}${blurred ? '80' : ''}`,
        color: secondaryContrastColor,
        borderColor: primaryColor || 'var(--color-primary-600)',
      }}
    >
      <div className='flex flex-col gap-3'>
        <p className='font-semibold' style={{ color: primaryColor || 'var(--color-primary-600)' }}>
          {name}
        </p>

        <div className='flex flex-col gap-2 justify-end h-auto md:h-24'>
          {Boolean(valueFrom) && (
            <div>
              <p className='leading-none font-normal opacity-70'>
                <span className='text-[0.6rem]'>De</span>
                <br />
                <span className='line-through'>{formatCentsToReais(valueFrom || 0)}</span>
                {monthly && <span className='text-xs no-underline font-normal'>/mês</span>}
              </p>
            </div>
          )}

          <div>
            <p className='text-xs'>A partir de *</p>
            <p className='text-4xl font-semibold'>
              {formatCentsToReais(value || 0)}
              {monthly && <span className='text-sm font-normal'>/mês</span>}
            </p>
          </div>
        </div>
      </div>

      <div className='flex flex-col gap-4'>
        <hr className='border-none h-px' style={{ backgroundColor: primaryColor || 'var(--color-primary-600)' }} />

        <PlanBenefits
          title={planBenefitsTitle || 'Disponível no plano:'}
          benefits={customPlanBenefits || planBenefits}
          planName={name}
          dark={secondaryContrastColor !== DEFAULT_LIGHT_CONTRAST_COLOR}
        />

        <div className='w-full'>
          <p className='text-right text-[0.7rem] font-light'>* Consulte condições</p>
          <ContactButton
            redirectToTarget={redirectToTarget}
            componentName='PlanCard'
            description={metaDescription}
            label='Entre em contato'
            targetId='contact-section'
            className='w-full mt-2'
            colorSchema='styled'
            style={{
              backgroundColor: primaryColor || 'var(--color-primary-600)',
              color: primaryContrastColor,
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default PlanCard
